import { useCloseDeletedSelection } from "../../components/common/useCloseDeletedSelection";
import { faCircleNotch, faEdit, faFile, faFolder, faPlus, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Form, Alert, Col, FormLabel, FormSelect, Row, Button, InputGroup } from "react-bootstrap";
import { ILanguage, ISiteDetails, ISiteMenuGroup, ISiteMenuItem, ISiteMenuTree } from "../../../models/Interfaces";
import { ContentService } from "../../../services/ContentService";
import { SiteMenuService } from "../../../services/SiteMenuService";
import { useEffect, useRef, useState } from "react";
import MenuItemEditor from "./MenuItemEditor";
import MenuGroupEditor from "./MenuGroupEditor";
import AppModal from "../../components/common/AppModal";
import InputGroupText from "react-bootstrap/esm/InputGroupText";
import { useConfirm } from "../../../config/ConfirmProvider";
import GenericTreeView, { INodeTypeDefinition, ITreeContextMenuItem } from "../../components/common/GenericTreeView";
import { SiteService } from "../../../services/SiteService";
import { TreeItemSort } from "../../../config/Enums";
import styles from "./menus.module.scss";

export enum MenuType {
  MenuGroup = "menugroup",
  MenuItem = "menuitem"
};

const Menus = () => {
  const [ loading, setLoading ] = useState<boolean>(true);
  const [ languages, setLanguages ] = useState<Array<ILanguage>>([]);
  const [ menus, setMenus ] = useState<Array<ISiteMenuTree>>([]);
  const [ selectedLang, setSelectedLang ] = useState<string | null>(null);
  const [ selectedTreeItem, setSelectedTreeItem ] = useState<ISiteMenuTree | null>(null);
  const [ selectedMenuItem, setSelectedMenuItem ] = useState<ISiteMenuItem | null>(null);
  const [ selectedMenuGroup, setSelectedMenuGroup ] = useState<ISiteMenuGroup | null>(null);
  const [ siteDetails, setSiteDetails ] = useState<ISiteDetails | null>(null);
  const [ showAddEditGroup, setShowAddEditGroup ] = useState<boolean> (false);
  const [ showAddEditItem, setShowAddEdititem ] = useState<boolean>(false);
  
  const contentSvcRef = useRef<ContentService | null>(null);
  const siteSvcRef = useRef<SiteService | null>(null);
  const menuSvcRef = useRef<SiteMenuService | null>(null);

  const { requestConfirmation } = useConfirm();

  const getSiteDetails = async () : Promise<void> => {
    if (siteSvcRef.current) {
      const res = await siteSvcRef.current.getSiteDetails();
      if (res) {
        setSiteDetails(res);
      }
    }
  };

  const getLanguages = async () : Promise<void> => {
    if (contentSvcRef.current) {
      const langs = await contentSvcRef.current.getSiteLanguages();

      if (langs?.length > 0) {
        setLanguages(langs);
      }
    }
  };

  const getMenusForLanguage = async () : Promise<void> => {
    if (menuSvcRef.current && selectedLang) {
      const menus = await menuSvcRef.current.getSiteMenusForLanguage(selectedLang);

      if (menus && menus.length > 0) {
        setMenus(menus);
      } else {
        setMenus([]);
      }
    }
  };

  const getMenuGroupItem = async (id: string) : Promise<ISiteMenuGroup | null> => {
    if (menuSvcRef.current && selectedLang) {
      const group = await menuSvcRef.current.getSiteMenuGroup(id);

      if (group) {
        return group;
      } 
    }
    return null;
  };

  const getMenuItem = async (id: string) : Promise<ISiteMenuItem | null> => {
    if (menuSvcRef.current) {
      const menuitem = await menuSvcRef.current.getSiteMenuItem(id);

      if (menuitem) {
        return menuitem;
      } 
    }
    return null;
  };

  const onLanguageSelect = async (event: React.ChangeEvent<HTMLSelectElement>) : Promise<void> => {
    const value = event.target.value;
    setSelectedLang(value);
  };

  const handleTreeSelect = async (item: ISiteMenuTree | null) : Promise<void> => {
    if (item) {
      setSelectedTreeItem(item);
    }
  };

  const handleAddGroup = () : void => {
    if (menuSvcRef.current && selectedLang) {
      const newgroup = menuSvcRef.current.newMenuGroup(selectedLang);
      setSelectedMenuGroup(newgroup);
      showHideEditor(MenuType.MenuGroup);
    }
  };

  const handleSaveMenuGroup = async (group: ISiteMenuGroup) : Promise<void> => {
    if (menuSvcRef.current) {
      const res = await menuSvcRef.current.saveMenuGroup(group);
      if (res) {  
        const saveditem = menuSvcRef.current.menuToTreeItem(res);
        const existing = menus.find(m => m.id === res.MenuGroupId);

        if (existing) {
          if (saveditem) {
            const filtered = menus.map(itm => itm.id === saveditem.id ? saveditem : itm);
            setMenus(filtered);
          }
        } else {
          if (saveditem) {
            setMenus(prev => ([
              ...prev,
              { ...saveditem }
            ]));
          }
        }
      }
      setSelectedMenuGroup(null);
      showHideEditor();
    }
  };

  const handleAddMenu = async (selected: any): Promise<void> => {

    if (selected && menuSvcRef.current) {
      let newitem: ISiteMenuItem | null = null;

      if (selected.type === MenuType.MenuGroup) {
        const group = await menuSvcRef.current.getSiteMenuGroup(selected.id);

        if (group) {
          newitem = menuSvcRef.current.newMenuItem(group.Language, undefined, selected.id);
        }
      } else {
        const currentitem = await menuSvcRef.current.getSiteMenuItem(selected.id);

        if (currentitem) {
          newitem = menuSvcRef.current.newMenuItem(currentitem.Language, currentitem.Id, currentitem.MenuGroupId ?? "");
        }
      }
      if (newitem) {
        setSelectedMenuItem(newitem);
        showHideEditor(MenuType.MenuItem);
      }
    }
  };

  const handleCloseEdit = () : void => {
    setSelectedMenuGroup(null);
    setSelectedMenuItem(null);
    setSelectedTreeItem(null);
    showHideEditor();
  };

  const closeDeletedGroup = useCloseDeletedSelection(selectedMenuGroup, (item) => String(item.MenuGroupId), () => {
    setSelectedMenuGroup(null);
    setShowAddEditGroup(false);
  });
  const closeDeletedItem = useCloseDeletedSelection(selectedMenuItem, (item) => String(item.Id), () => {
    setSelectedMenuItem(null);
    setShowAddEdititem(false);
  });

  const handleDeleteMenu = async (selected: any): Promise<void> => {
    if (menuSvcRef.current && selected) {
      const itemtype = selected.type === MenuType.MenuGroup ? "menu group" : "menu item";
      let removed: boolean = false;
      const confirmed = await requestConfirmation(`Delete the selected ${itemtype}? This cannot be undone.`, `Remove ${itemtype}`)

      if (confirmed) {
        if (selected.type === MenuType.MenuGroup) {
          removed = await menuSvcRef.current.deleteMenuGroup(selected.id);
        } else {
          const id = Number(selected.id);
          removed = await menuSvcRef.current.deleteMenuItem(id);
        }

        if (removed) {
          const removedIds = new Set([String(selected.id)]);
          // A removed parent also removes its descendants from the visible tree.
          let previousSize = 0;
          while (previousSize !== removedIds.size) {
            previousSize = removedIds.size;
            menus.forEach((item) => {
              if (removedIds.has(String(item.parent))) removedIds.add(String(item.id));
            });
          }
          setMenus((current) => current.filter((item) => !removedIds.has(String(item.id))));
          removedIds.forEach((id) => { closeDeletedGroup(id); closeDeletedItem(id); });
          setSelectedTreeItem((current) => current && removedIds.has(String(current.id)) ? null : current);
        }
      }
    }
  };

  const handleEditMenu = async (selected: any): Promise<void> => {
    if (selected && menuSvcRef.current) {
      if (selected.type === MenuType.MenuGroup) {
        const existinggrp = await menuSvcRef.current.getSiteMenuGroup(selected.id);

        if (existinggrp) {
          setSelectedMenuGroup(existinggrp);
          showHideEditor(MenuType.MenuGroup);
        }

      } else {
        const existingitem = await menuSvcRef.current.getSiteMenuItem(selected.id);

        if (existingitem) {
          setSelectedMenuItem(existingitem);
          showHideEditor(MenuType.MenuItem);
        }
      }
    }
  };

  const handleSaveMenuItem = async (menuitem: ISiteMenuItem) : Promise<void> => {
    if (menuSvcRef.current && menuitem) {
      const saveres = await menuSvcRef.current.saveMenuItem(menuitem);
      
      if (saveres) {  
        const saveditem = menuSvcRef.current.menuToTreeItem(saveres);
        const existing = menus.find(m => m.id.toString() === saveres.Id.toString());

        if (existing) {
          if (saveditem) {
            const filtered = menus.map(itm => itm.id === saveditem.id ? saveditem : itm);
            setMenus(filtered);

            switch (saveditem.type) {
              case MenuType.MenuGroup:
                //setSelectedMenuGroup(saveres);
                break;
              case MenuType.MenuItem:
                setSelectedMenuItem(saveres);
                break;
            }
          }
        } else {
          if (saveditem) {
            setMenus(prev => ([
              ...prev,
              { ...saveditem }
            ]));
          }
        }
      }
    }
    showHideEditor();
  };

  const createTreeCtxMenus = () : Array<ITreeContextMenuItem>  => {
    const menus = [
      { ItemText: "Add Menu", callbackFunction: (n: any) => handleAddMenu(n), ItemIcon: <FontAwesomeIcon icon={faPlus} /> },
      { ItemText: "Edit Menu", callbackFunction: (n: any) => handleEditMenu(n), ItemIcon: <FontAwesomeIcon icon={faEdit} /> },
      { ItemText: "Delete Menu", callbackFunction: (n: any) => handleDeleteMenu(n), ItemIcon: <FontAwesomeIcon icon={faTimes} /> }
    ];
    return menus;
  };

  const showHideEditor = (type?: MenuType) => {
    if (type) {
      if (type === MenuType.MenuGroup) {
        setShowAddEdititem(false);
        setShowAddEditGroup(true);
      } else {
        setShowAddEditGroup(false);
        setShowAddEdititem(true);
      }
    } else {
      setShowAddEditGroup(false);
      setShowAddEdititem(false);
    }
  };

  const getLanguageName = (shortCode: string | null | undefined): string => {
    if (!shortCode) {
      return "Not set";
    }
    return languages.find((lang) => lang.ShortCode.toLowerCase() === shortCode.toLowerCase())?.Language ?? shortCode;
  };

  const getSelectedTreeChildrenCount = (): number => {
    if (selectedMenuGroup?.MenuItems?.length) {
      return selectedMenuGroup.MenuItems.length;
    }

    if (selectedTreeItem?.children?.length) {
      return selectedTreeItem.children.length;
    }

    if (selectedMenuItem?.Id) {
      return menus.filter((item) => item.parent === selectedMenuItem.Id.toString()).length;
    }

    return 0;
  };

  const openSelectedEditor = async (): Promise<void> => {
    if (!selectedTreeItem) {
      return;
    }
    await handleEditMenu(selectedTreeItem);
  };

  const deleteSelectedNode = async (): Promise<void> => {
    if (!selectedTreeItem) {
      return;
    }
    await handleDeleteMenu(selectedTreeItem);
  };

  const addChildToSelection = async (): Promise<void> => {
    if (!selectedTreeItem) {
      return;
    }
    await handleAddMenu(selectedTreeItem);
  };

  const treeNodeDefinition = () : Array<INodeTypeDefinition> => {
    return [
      {
        parentPropertyName: "parent",
        displayPropertyName: "text",
        nodeTypeValue: "menugroup",
        typeIconDefinition: faFolder,
        onItemSelect: handleTreeSelect,
        // contextMenu: ArrayITreeContextMenuItem 
      },
      {
        parentPropertyName: "parent",
        displayPropertyName: "text",
        nodeTypeValue: "menuitem",
        typeIconDefinition: faFile,
        onItemSelect: handleTreeSelect
      }
    ]
  };

  useEffect(() => {
    if (selectedLang) {
      getMenusForLanguage();
    }
  }, [selectedLang]);

  useEffect(() => {
    if (siteDetails && siteDetails.DefaultLanguage) {
      setSelectedLang(siteDetails.DefaultLanguage);
    }
  }, [siteDetails]);

  useEffect(() => {
    let active = true;
    if (selectedTreeItem) {
      const getgroup = async () : Promise<void> => {
        const group = await getMenuGroupItem(selectedTreeItem.id);
        if (!active) return;
        setSelectedMenuItem(null);
        setSelectedMenuGroup(group);
      }

      const getitem = async () : Promise<void> => {
        const menu = await getMenuItem(selectedTreeItem.id);
        if (!active) return;
        setSelectedMenuGroup(null);
        setSelectedMenuItem(menu);
      }

      switch(selectedTreeItem.type) {
        case MenuType.MenuGroup:
          setSelectedMenuGroup(null);
          getgroup();
          break;
        case MenuType.MenuItem:
          setSelectedMenuItem(null);
          getitem();
          break;
      }
    } 
    return () => { active = false; };
  }, [selectedTreeItem])

  useEffect(() => {
    if (!siteSvcRef.current) {
      siteSvcRef.current = new SiteService();
      getSiteDetails();
    }

    if (!contentSvcRef.current) {
      contentSvcRef.current = new ContentService();
      getLanguages();
    }

    if (!menuSvcRef.current) {
      menuSvcRef.current = new SiteMenuService();
    }

    setLoading(false);
  }, []);

  return (
    <>
      {loading ?
        (
          <Row>
            <Col xs={12} className="p-5 text-center vstack">
              <FontAwesomeIcon icon={faCircleNotch} size="5x" spin={true} className="text-secondary" />
              <span className="mt-3 text-secondary">loading...</span>
            </Col>
          </Row>
        )
        :
        (
          <div className={styles.page}>
            <section className="app-list-section">
              <div className="app-list-section__header">
                <div>
                  <h2 className="app-list-section__title">Site Menus</h2>
                  <p className="app-list-section__summary">
                    Organise menu groups and menu items by language, then open the selected entry only when you need to edit its full details.
                  </p>
                </div>
              </div>
              <div className="app-list-section__body">
                <div className={styles.toolbar}>
                  <div className={styles.toolbarField}>
                    <FormLabel className={styles.toolbarLabel}>Select site language</FormLabel>
                    <InputGroup>
                      <FormSelect
                        title="select language"
                        value={selectedLang ?? ""}
                        onChange={onLanguageSelect}
                      >
                        <option key="defaultlang" value={""}>select site language...</option>
                        {languages.map(lang => (
                          <option key={lang.ShortCode} value={lang.ShortCode}>{lang.Language}</option>
                        ))}
                      </FormSelect>
                    </InputGroup>
                  </div>
                  <div className={styles.toolbarActions}>
                    <Button variant="outline-primary" onClick={handleAddGroup} disabled={!selectedLang}>
                      <FontAwesomeIcon icon={faPlus} /> Add Menu Group
                    </Button>
                  </div>
                </div>

                {menus.length > 0 ? (
                  <div className={styles.workspace}>
                    <section className={styles.panel} id="menu-item-tree">
                      <div className={styles.panelHeader}>
                        <div>
                          <h3 className={styles.panelTitle}>Menu Structure</h3>
                          <p className={styles.panelSubtle}>Select a group or item to review its summary and available actions.</p>
                        </div>
                      </div>
                      <div className={styles.panelDivider} aria-hidden="true"></div>
                      <div className={styles.treeHost}>
                        <div className={styles.treeShell}>
                          <GenericTreeView
                            idPropertyName="id"
                            nodeTypeDefinitions={treeNodeDefinition()}
                            value={""}
                            items={menus}
                            style={{ flexGrow: 1 }}
                            rootNodeKeyValue={"#"}
                            closeOnSelect={false}
                            contextMenuItems={createTreeCtxMenus()}
                            typePropertyName="type"
                            sortBy={TreeItemSort.Unsorted}
                          />
                        </div>
                      </div>
                    </section>

                    <section className={styles.panel}>
                      <div className={styles.panelHeader}>
                        <div>
                          <h3 className={styles.panelTitle}>Selection</h3>
                          <p className={styles.panelSubtle}>Edit only when needed. Pick a node to review it here before opening the full editor.</p>
                        </div>
                      </div>
                      <div className={styles.panelBody}>
                        {!selectedTreeItem && (
                          <div className={styles.emptyState}>
                            <span className={styles.emptyBadge}>Selection Required</span>
                            <h4 className={styles.emptyTitle}>No Menu Item Selected</h4>
                            <p className={styles.emptyText}>
                              Choose a menu group or menu item from the tree to view its details here. This keeps the screen lighter and avoids opening the full editor until it is needed.
                            </p>
                            <div className={styles.actionRow}>
                              <Button variant="outline-primary" onClick={handleAddGroup} disabled={!selectedLang}>
                                <FontAwesomeIcon icon={faPlus} /> Add Menu Group
                              </Button>
                            </div>
                          </div>
                        )}

                        {selectedTreeItem && selectedMenuGroup && (
                          <div className={styles.summaryCard}>
                            <div className={styles.summaryLead}>
                              <div className={styles.summaryBadge}>Menu Group</div>
                              <h4 className={styles.summaryTitle}>{selectedMenuGroup.Name || "Untitled group"}</h4>
                            </div>
                            <div className={styles.summaryMeta}>
                              <div className={styles.metaItem}>
                                <span className={styles.metaLabel}>Language</span>
                                <span className={styles.metaValue}>{getLanguageName(selectedMenuGroup.Language)}</span>
                              </div>
                              <div className={styles.metaItem}>
                                <span className={styles.metaLabel}>Status</span>
                                <span className={styles.metaValue}>{selectedMenuGroup.Enabled ? "Enabled" : "Disabled"}</span>
                              </div>
                              <div className={styles.metaItem}>
                                <span className={styles.metaLabel}>Alignment</span>
                                <span className={styles.metaValue}>{selectedMenuGroup.Alignment || "Not set"}</span>
                              </div>
                              <div className={styles.metaItem}>
                                <span className={styles.metaLabel}>Items</span>
                                <span className={styles.metaValue}>{getSelectedTreeChildrenCount()}</span>
                              </div>
                              <div className={styles.metaItem}>
                                <span className={styles.metaLabel}>Sticky Menu</span>
                                <span className={styles.metaValue}>{selectedMenuGroup.KeepAtTop ? "Yes" : "No"}</span>
                              </div>
                              <div className={styles.metaItem}>
                                <span className={styles.metaLabel}>Shadow</span>
                                <span className={styles.metaValue}>{selectedMenuGroup.Shadow ? "On" : "Off"}</span>
                              </div>
                            </div>
                            <div className={styles.actionRow}>
                              <Button variant="primary" onClick={openSelectedEditor}>
                                <FontAwesomeIcon icon={faEdit} /> Edit Group
                              </Button>
                              <Button variant="outline-primary" onClick={addChildToSelection}>
                                <FontAwesomeIcon icon={faPlus} /> Add Menu Item
                              </Button>
                              <Button variant="outline-danger" onClick={deleteSelectedNode}>
                                <FontAwesomeIcon icon={faTimes} /> Delete
                              </Button>
                            </div>
                          </div>
                        )}

                        {selectedTreeItem && selectedMenuItem && (
                          <div className={styles.summaryCard}>
                            <div className={styles.summaryLead}>
                              <div className={styles.summaryBadge}>Menu Item</div>
                              <h4 className={styles.summaryTitle}>{selectedMenuItem.DisplayText || "Untitled item"}</h4>
                            </div>
                            <div className={styles.summaryMeta}>
                              <div className={styles.metaItem}>
                                <span className={styles.metaLabel}>Language</span>
                                <span className={styles.metaValue}>{getLanguageName(selectedMenuItem.Language)}</span>
                              </div>
                              <div className={styles.metaItem}>
                                <span className={styles.metaLabel}>Status</span>
                                <span className={styles.metaValue}>{selectedMenuItem.Enabled ? "Enabled" : "Disabled"}</span>
                              </div>
                              <div className={styles.metaItem}>
                                <span className={styles.metaLabel}>Target</span>
                                <span className={styles.metaValue}>{selectedMenuItem.Target || "Not set"}</span>
                              </div>
                              <div className={styles.metaItem}>
                                <span className={styles.metaLabel}>Children</span>
                                <span className={styles.metaValue}>{getSelectedTreeChildrenCount()}</span>
                              </div>
                              <div className={styles.metaItem}>
                                <span className={styles.metaLabel}>URL</span>
                                <span className={styles.metaValue}>{selectedMenuItem.Url || "No URL selected"}</span>
                              </div>
                              <div className={styles.metaItem}>
                                <span className={styles.metaLabel}>Display Order</span>
                                <span className={styles.metaValue}>{selectedMenuItem.DisplayOrder ?? "Not set"}</span>
                              </div>
                            </div>
                            <div className={styles.actionRow}>
                              <Button variant="primary" onClick={openSelectedEditor}>
                                <FontAwesomeIcon icon={faEdit} /> Edit Item
                              </Button>
                              <Button variant="outline-primary" onClick={addChildToSelection}>
                                <FontAwesomeIcon icon={faPlus} /> Add Child Item
                              </Button>
                              <Button variant="outline-danger" onClick={deleteSelectedNode}>
                                <FontAwesomeIcon icon={faTimes} /> Delete
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    </section>
                  </div>
                ) : (
                  <Alert variant="info" className="m-0">
                    <div className={styles.emptyState}>
                      <span className={styles.emptyBadge}>Get Started</span>
                      <h4 className={styles.emptyTitle}>No menus configured for this language</h4>
                      <p className={styles.emptyText}>
                        Start with a menu group, then add items beneath it. This keeps the structure clear before you move into the detailed menu item settings.
                      </p>
                      <div className={styles.actionRow}>
                        <Button variant="outline-primary" onClick={handleAddGroup} disabled={!menuSvcRef.current || !selectedLang}>
                          <FontAwesomeIcon icon={faPlus} /> Add Menu Group
                        </Button>
                      </div>
                    </div>
                  </Alert>
                )}
              </div>
            </section>
          </div>
        )
      }
      { showAddEditGroup && menuSvcRef.current && selectedMenuGroup && (
        <AppModal 
          onClose={() => setShowAddEditGroup(false)}
          showButtons={false}
          size="xl"
          title="Menu Group Details">
          <MenuGroupEditor 
            onClose={() => setShowAddEditGroup(false)} 
            selectedGroupItem={selectedMenuGroup}
            onSave={handleSaveMenuGroup}
            asModal={true}
          />
        </AppModal>
      )}

      { showAddEditItem && menuSvcRef.current && selectedMenuItem && (
        <AppModal
          onClose={() => setShowAddEdititem(false)}
          showButtons={false}
          size={"xl"}
          title="Menu Item Details">
          <MenuItemEditor 
            onClose={() => setShowAddEdititem(false)}
            onSave={handleSaveMenuItem}
            selectedMenuItem={selectedMenuItem} 
            languages={languages}
            asModal={true}
          />
        </AppModal>
      )}
    </>
  );
}

export default Menus;
